<template>
    <a
        v-if="productData"
        class="map-product-card font-sans"
        :href="link"
        :data-product-id="(productData as IHotelProduct).hotel_id ?? productData.id"
        target="_blank"
        external
    >
        <div :class="[optimizedImage.url ? 'image-logo' : 'image']">
            <LazyClientOnly>
                <NuxtImg
                    :src="optimizedImage.large ?? optimizedImage.url"
                    :srcset="optimizedImage.url ? '' : `${optimizedImage.large}, ${optimizedImage.medium}`"
                    alt="Product Image"
                    loading="lazy"
                    :style="{ 'object-fit': optimizedImage.url ? 'contain' : 'cover', width: '100%', height: '100%' }"
                    densities="x1"
                />
            </LazyClientOnly>
        </div>
        <div class="content">
            <div class="info">
                <div class="info-container">
                    <h5 class="title">
                        {{ productData.name }}
                    </h5>
                    <div
                        v-if="(productData as IHotelProduct).overview || (productData as IParkingProduct).ratings"
                        class="rating"
                    >
                        <div class="rating-info">
                            <span class="review-count"
                                >{{
                                    (productData as IHotelProduct).overview?.surveys_count ??
                                    (productData as IParkingProduct).ratings?.rating_count ??
                                    0
                                }}
                                {{ te('reviews') ? t('reviews') : 'Reviews' }}</span
                            >
                        </div>
                    </div>
                </div>
                <div class="button-container">
                    <div class="link-button">
                        {{ te('viewDeal') ? t('viewDeal') : 'View Deal' }}
                    </div>
                </div>
            </div>
        </div>
    </a>
</template>

<script setup lang="ts">
import type { IHotelProduct, IParkingProduct } from '@nuxt-monorepo/ui/types';
const props = defineProps<{
    productData: IHotelProduct | IParkingProduct;
}>();
const { t, te } = useI18n();

const isIHotelProduct = (product: IHotelProduct | IParkingProduct): product is IHotelProduct => {
    return (product as IParkingProduct).geo?.latitude === undefined;
};

const img = useImage();
const optimizedImage = computed(() => {
    const largeImgUrl = isIHotelProduct(props.productData)
        ? img(`${props.productData.images?.large_url}/${props.productData.images?.data[0]}`, {
              format: 'webp',
              height: 200,
          })
        : undefined;
    const mediumImgUrl = isIHotelProduct(props.productData)
        ? img(`${props.productData.images?.medium_url}/${props.productData.images?.data[0]}`, {
              format: 'webp',
              height: 200,
          })
        : undefined;
    const url = !isIHotelProduct(props.productData)
        ? img(props.productData.media?.logo, { format: 'webp', height: 200 })
        : undefined;
    return { large: largeImgUrl, medium: mediumImgUrl, url };
});

function relativeLink(link: string) {
    const fullUrl = new URL(link);
    return fullUrl.pathname;
}
const link = computed(() =>
    isIHotelProduct(props.productData)
        ? props.productData.info?.url
        : props.productData.content.psf_url
          ? relativeLink(props.productData.content.psf_url).trim()
          : '/' + props.productData.content.slug
);
</script>

<style lang="postcss" scoped>
.map-product-card {
    @apply text-xs font-light text-gray-800 no-underline relative flex flex-col bg-white rounded overflow-hidden w-full h-full;
    word-wrap: break-word;

    &:hover {
        .title {
            @apply text-primary underline;
        }
    }
    .rating-info {
        @apply inline-block relative text-gray-400;
    }
    .rating {
        @apply mb-1 text-sm leading-4 font-normal;
    }
    .image {
        @apply bg-center bg-cover bg-gray-300 w-full h-44;
        @media screen(sm) {
            height: 200px;
        }
    }
    .image-logo {
        @apply bg-center bg-contain bg-no-repeat bg-white w-full h-44;
    }
    .content {
        @apply p-3 flex flex-grow flex-col;
    }
    .info {
        @apply flex flex-grow flex-col;
    }
    .info-container {
        @apply mb-2;
    }
    .title {
        @apply mb-1 font-normal;
        transition: color ease-in-out 0.2s;
        font-size: 17px;
    }
    .button-container {
        @apply flex justify-end mt-auto;
    }
    .link-button {
        @apply inline-flex justify-center rounded text-lg text-center font-medium cursor-pointer px-6 py-1 text-white bg-btn border-btn-border;
        min-width: 198px;
        &:hover {
            @apply bg-btn-hover;
        }
    }
}
</style>
